import { Link } from '@tanstack/react-router';
import { WidgetContainer } from '@/components/analytics/WidgetContainer';
import type { QuizletPerformance, MasteryLevel, QuizletMastery } from '@/types/analytics';
import styles from './BestWorstPerforming.module.scss';

export interface BestWorstPerformingProps {
  data: (QuizletPerformance | QuizletMastery)[] | null;
  isLoading?: boolean;
  error?: Error | null;
  limit?: number;
}

/**
 * Best & Worst Performing Quizlets Widget
 *
 * Split view showing top and bottom performing quizlets.
 * Helps identify strengths and areas needing improvement.
 */
export function BestWorstPerforming({
  data,
  isLoading,
  error,
  limit = 5,
}: BestWorstPerformingProps) {
  const isEmpty = !data || data.length === 0;

  // Sort by success rate
  const sortedData = data ? [...data].sort((a, b) => b.successRate - a.successRate) : [];
  const bestPerforming = sortedData.slice(0, limit);
  const worstPerforming = sortedData.slice(-limit).reverse();

  // Get medal emoji for mastery level
  const getMasteryEmoji = (level: MasteryLevel): string => {
    switch (level) {
      case 'gold':
        return '🥇';
      case 'silver':
        return '🥈';
      case 'bronze':
        return '🥉';
      default:
        return '⚪';
    }
  };

  return (
    <WidgetContainer
      title="Best & Worst Performing"
      description="Top and bottom quizlets by success rate"
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage="Practice quizlets to see performance rankings"
      size="large"
    >
      <div className={styles.performance}>
        {/* Best Performing */}
        <div className={styles.performance__section}>
          <h4 className={styles.performance__sectionTitle}>
            <span className={styles.performance__sectionIcon}>🌟</span>
            Top Performers
          </h4>
          <div className={styles.performance__list}>
            {bestPerforming.map((quizlet, index) => (
              <Link
                key={quizlet.quizletId}
                to="/analytics/$quizletId"
                params={{ quizletId: quizlet.quizletId }}
                className={styles.performance__item}
              >
                <span className={styles.performance__rank}>#{index + 1}</span>
                <span className={styles.performance__emoji}>
                  {getMasteryEmoji(quizlet.masteryLevel)}
                </span>
                <div className={styles.performance__info}>
                  <span className={styles.performance__name}>{quizlet.quizletName}</span>
                  <span className={styles.performance__stats}>
                    {'correctQuestions' in quizlet
                      ? `${quizlet.correctQuestions}/${quizlet.totalQuestions} correct`
                      : `${quizlet.masteredQuestions}/${quizlet.totalQuestions} mastered`}
                  </span>
                </div>
                <span className={styles.performance__rate}>
                  {quizlet.successRate.toFixed(0)}%
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className={styles.performance__divider} />

        {/* Worst Performing */}
        <div className={styles.performance__section}>
          <h4 className={styles.performance__sectionTitle}>
            <span className={styles.performance__sectionIcon}>💪</span>
            Needs Practice
          </h4>
          <div className={styles.performance__list}>
            {worstPerforming.map((quizlet, index) => (
              <Link
                key={quizlet.quizletId}
                to="/analytics/$quizletId"
                params={{ quizletId: quizlet.quizletId }}
                className={styles.performance__item}
              >
                <span className={styles.performance__rank}>#{sortedData.length - index}</span>
                <span className={styles.performance__emoji}>
                  {getMasteryEmoji(quizlet.masteryLevel)}
                </span>
                <div className={styles.performance__info}>
                  <span className={styles.performance__name}>{quizlet.quizletName}</span>
                  <span className={styles.performance__stats}>
                    {'correctQuestions' in quizlet
                      ? `${quizlet.correctQuestions}/${quizlet.totalQuestions} correct`
                      : `${quizlet.masteredQuestions}/${quizlet.totalQuestions} mastered`}
                  </span>
                </div>
                <span className={styles.performance__rate}>
                  {quizlet.successRate.toFixed(0)}%
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </WidgetContainer>
  );
}
